// @ts-nocheck
/* global Canny */
import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import css from '../shared/css';
import Button from '../shared/button';
import ButtonIcon from '../components/button';
import ActivityCenter from './activity-center';
import Menu from '../shared/menu';
import GlobalNavOrg from './global-nav-org';

import {
  AppIcon,
  ArrowDownIcon,
  BoltIcon,
  ChoiceListIcon,
  ClassificationSetIcon,
  ExportsIcon,
  ImportsIcon,
  LayerIcon,
  PlusIcon,
  ProjectIcon,
  QuestionIcon,
  ShareIcon,
} from '../icons';
import { usePermissions } from '../contexts/session-context';
import { useAppDispatch, useAppSelector } from '../hooks';
import * as actions from './batch-menu.slice';
import * as selectors from './batch-menu.selectors';

const StyledButtonIcon = styled(ButtonIcon)(({ theme }) => ({
  padding: 0,
  '&:hover': {
    color: theme.palette.common.black,
  },
}));

// Additional Menu Item Styles
const menuTitle = {
  background: '#fff', paddingTop: 3, paddingBottom: 8, borderBottom: '1px solid #eee', cursor: 'default', pointerEvents: 'none',
};

// Help Menu Items
const helpItems = [
  { label: 'Help', className: 'subheading', style: { ...menuTitle } },
  {
    icon: ShareIcon, iconWidth: 13, iconStyle: { fill: '#bbb' }, label: 'Fulcrum Academy', onClick: () => window.open('https://academy.fulcrumapp.com', '_blank', 'noreferrer'),
  },
  {
    icon: ShareIcon, iconWidth: 13, iconStyle: { fill: '#bbb' }, label: 'Help Center', onClick: () => window.open('https://help.fulcrumapp.com', '_blank', 'noreferrer'),
  },
  {
    icon: ShareIcon, iconWidth: 13, iconStyle: { fill: '#bbb' }, label: 'Feedback', onClick: () => window.open('https://feedback.fulcrumapp.com/feature-requests', '_blank', 'noreferrer'),
  },
  {
    icon: ShareIcon, iconWidth: 13, iconStyle: { fill: '#bbb' }, label: 'Developer Docs', onClick: () => window.open('https://docs.fulcrumapp.com/', '_blank', 'noreferrer'),
  },
  {
    icon: ShareIcon, iconWidth: 13, iconStyle: { fill: '#bbb' }, label: 'Webinars & Events', onClick: () => window.open('https://www.fulcrumapp.com/webinars-and-events/', '_blank', 'noreferrer'),
  },
  {
    icon: ShareIcon, iconWidth: 13, iconStyle: { fill: '#bbb' }, label: 'App Gallery', onClick: () => window.open('https://www.fulcrumapp.com/apps-gallery/', '_blank', 'noopener,noreferrer'),
  },
];

const GlobalNav = () => {
  const permissions = usePermissions();
  const batchServiceEnabled = permissions.canBulkDeleteRecords || permissions.canBulkUpdateRecords;

  const [toggleNew, setToggleNew] = useState(false);
  const [toggleHelp, setToggleHelp] = useState(false);
  const openBatchProcessMenu = useAppSelector(selectors.openBatchProcessSelector);
  const dispatch = useAppDispatch();

  const showNew = permissions.canManageApps
    || permissions.canImportRecords
    || permissions.canExportRecords
    || permissions.canManageProjects
    || permissions.canManageLayers
    || permissions.canManageChoiceLists
    || permissions.canManageClassificationSets;

  // 'New' Button Menu Items
  const newItems = [
    {
      icon: AppIcon, label: 'App', onClick: () => window.open('/apps/new', '_self'), hidden: !permissions.canManageApps,
    },
    {
      icon: ImportsIcon, label: 'Import', onClick: () => window.open('/imports/new', '_self'), hidden: !permissions.canImportRecords,
    },
    {
      icon: ExportsIcon, label: 'Export', onClick: () => window.open('/exports/new', '_self'), hidden: !permissions.canExportRecords,
    },
    {
      icon: ProjectIcon, label: 'Project', onClick: () => window.open('/projects/new', '_self'), hidden: !permissions.canManageProjects,
    },
    {
      icon: LayerIcon, label: 'Map Layers', onClick: () => window.open('/layers/new', '_self'), hidden: !permissions.canManageLayers,
    },
    {
      icon: ChoiceListIcon, label: 'Choice List', onClick: () => window.open('/choice_lists/new', '_self'), hidden: !permissions.canManageChoiceLists,
    },
    {
      icon: ClassificationSetIcon, label: 'Classification Set', onClick: () => window.open('/classification_sets/new', '_self'), hidden: !permissions.canManageClassificationSets,
    },
  ];

  const escFunction = useCallback((event) => {
    if (event.keyCode === 27) {
      setToggleNew(false);
      setToggleHelp(false);
      Canny('closeChangelog');
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    Canny('initChangelog', {
      appID: '5cacd056827ec244d59de1e6',
      position: 'bottom',
      align: 'right',
    });

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, []);

  const handleToggleBatchProcess = (e: HTMLButtonElement) => {
    if (e.target.tagName === 'BUTTON') {
      return;
    }

    const element = openBatchProcessMenu ? null : e.currentTarget;
    dispatch(actions.setOpenBatchProcess(element));
  };

  return (
    <div className={styles.wrapper} data-testid="global-nav-wrapper">
      <div className={styles.container} data-testid="global-nav-container">
        <div className={cx(styles.buttonGroup, 'menu-group')} data-testid="button-group">
          {showNew
            && (
              <span className={styles.menu} data-testid="new-menu">
                <Button
                  quietOutlined
                  icon
                  className={styles.newButton}
                  onClick={() => setToggleNew(!toggleNew)}
                  data-testid="new-button"
                >
                  <PlusIcon width={11} style={{ display: 'flex' }} />
                  <ArrowDownIcon
                    width={10}
                    className={styles.arrowDownIcon}
                    data-testid="new-arrow-icon"
                  />
                </Button>
                <Menu
                  show={toggleNew}
                  onHide={() => setToggleNew(false)}
                  className={cx(styles.dropDownNew)}
                  items={newItems}
                  verticalOffset={8}
                  horizontalOffset={-9}
                  minWidth={160}
                  data-testid="new-menu-dropdown"
                />
              </span>
            )}
          <span className={styles.menu} data-testid="bolt-menu">
            <BoltIcon className={styles.actionIcons} width={20} data-canny-changelog data-testid="bolt-icon" />
          </span>
          <span className={styles.menu} data-testid="help-menu">
            <QuestionIcon
              onClick={() => setToggleHelp(!toggleHelp)}
              width={22}
              className={styles.actionIcons}
              data-testid="question-icon"
            />
            <Menu
              show={toggleHelp}
              onHide={() => setToggleHelp(false)}
              items={helpItems}
              className={styles.dropDownNew}
              verticalOffset={11}
              horizontalOffset={-165}
              minWidth={200}
              data-testid="help-menu-dropdown"
            />
          </span>
          {batchServiceEnabled && (
            <span className={styles.menu} data-testid="batch-menu">
              <StyledButtonIcon
                sx={{ fontSize: (theme) => theme.spacing(3.5) }}
                data-testid="activity-center-button"
                disableRipple
                onClick={handleToggleBatchProcess}
                icon={<AccessTimeIcon color="gray.light" fontSize="inherit" />}
              />
              <ActivityCenter />
            </span>
          )}
          <span className={styles.menu} data-testid="org-menu">
            <GlobalNavOrg data-testid="global-nav-org" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default GlobalNav;

const styles = css({
  wrapper: {
    height: 47,
    boxSizing: 'border-box',
    display: 'flex',
    padding: '6px 10px',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fff',
  },
  container: {
    background: '#fff',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
  },
  dropDownNew: {
    padding: '5px 0',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  menu: {
    marginLeft: 15,
  },
  actionIcons: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fill: '#7d7d7d',

    ':hover': {
      fill: '#41403b',
    },
  },
  newButton: {
    display: 'flex !important',
    margin: 0,
    alignItems: 'center',
  },
  arrowDownIcon: {
    display: 'flex',
    marginLeft: 10,
  },
});
